import { FilterExpression, like } from '@infuse/api/common';
import { FilterConfigurationType } from '@infuse/ui/domain';
import { useI18n } from 'vue-i18n';
import { AuditLog } from './model';

export interface Options {
  query?: string;
  filter?: FilterExpression;
  page: number;
  lastPage: boolean;
}

export interface State {
  items: AuditLog[];
  options: Options;
  loading: boolean;
  loadingGet: boolean;
}

export function useAuditLogFilters() {
  const { t } = useI18n({ useScope: 'global' });

  const successFilter = {
    name: 'success',
    type: FilterConfigurationType.RADIO,
    label: () => t('shared.audit.filter.success.label'),
    options: [
      {
        name: 'success',
        label: () => t('shared.audit.filter.success.success'),
        toUmbrellaExpression(): FilterExpression {
          return new FilterExpression().equals('success', true);
        }
      },
      {
        name: 'notSuccess',
        label: () => t('shared.audit.filter.success.notSuccess'),
        toUmbrellaExpression(): FilterExpression {
          return new FilterExpression().equals('success', false);
        }
      }
    ]
  };

  return {
    successFilter
  };
}

const createSearchFilter = (searchQuery: string, matchObject = false): FilterExpression | undefined => {
  if (searchQuery === '' && !matchObject) {
    return;
  }

  const filterOptions = searchQuery
    .toLowerCase()
    .replace('%', '\\%')
    .split(' ')
    .filter((part: string): boolean => part !== undefined && part !== null && part !== '')
    .map((part: string): string => `%${part}%`)
    .map((part: string): FilterExpression => {
      if (matchObject) {
        return new FilterExpression().and(like('lower(action)', part).or(like('lower(userId)', part)));
      }

      return new FilterExpression().and(
        like('lower(objectId)', part).or(like('lower(action)', part)).or(like('userId', part))
      );
    });

  if (filterOptions.length === 0) {
    return;
  }

  return filterOptions.reduce((acc, cv) => acc.and(cv), new FilterExpression());
};

export const aggregateSearchQueryAndFilters = (options: Options, matchObject = false): FilterExpression | undefined => {
  // combines regular filters with the search filter
  const searchFilter = createSearchFilter(options.query || '', matchObject);
  const filterCopy = new FilterExpression(); // copy is needed since FilterExpression methods alter the object itself
  filterCopy.operations = Object.assign([], options.filter?.operations); // shallow copy

  if (searchFilter) {
    return options.filter ? filterCopy.and(searchFilter) : searchFilter;
  } else {
    return options.filter;
  }
};
